/// <reference path="../../nativewind-augmentation.d.ts" />
// Presentational location-attachment card (the Figma "share location"
// result). A 380-wide, radius-16 wrap (indigo for self, dark surface
// otherwise) with a padding-2
// inner frame at radius 14. The body is a ~300×160 static-map preview and a
// footer with a MapPin glyph + label.
//
// Static map: if a maps key is wired into the web env we build a static-tile
// URL centered on `lat,lng` and render it via the RN `<Image>`. The PRIMARY
// contract is a generic provider:
//   VITE_MAPS_KEY        — the provider API key
//   VITE_MAPS_STATIC_URL — a URL template with {lon} {lat} {zoom} {w} {h}
//                          {key} placeholders, e.g.
//   https://api.maptiler.com/maps/streets/static/{lon},{lat},{zoom}/{w}x{h}.png?key={key}
// As a convenience we also keep the older provider-specific tokens
// (VITE_MAPBOX_TOKEN / VITE_GOOGLE_MAPS_KEY / VITE_MAPTILER_KEY) as a
// fallback when no template is configured. With NO key (the current default —
// none is configured) we render a styled placeholder: a muted surface with a
// centered MapPin and the coordinate text. We never embed a literal secret
// here; the key is only ever read from the host env at runtime. The
// `buildStaticMapUrl` helper is exported and reused by ShareLocationModal so
// the in-modal preview and the sent bubble share one URL contract.

import React, { useMemo } from 'react';
import { Image, Pressable, Text, View } from 'react-native';
import { MapPin } from 'lucide-react-native';
import { readMapsEnv } from './locationMapsEnv';
import { ReadReceiptOverlay, type ReadByReader } from '../ReadReceiptOverlay';
import { useTileMaxWidth } from '../tileConstraint';

export interface LocationAttachmentCardProps {
  /** Own + delivered → muted double-check beside the footer time. */
  delivered?: boolean | undefined;
  /** Own + read → green double-check (takes precedence over `delivered`). */
  read?: boolean | undefined;
  lat: number;
  lng: number;
  /** Optional human label; falls back to "Shared location". */
  label?: string | undefined;
  isSelf: boolean;
  /** Host opens the maps deep link, e.g. https://maps.google.com/?q=lat,lng */
  onOpen?: (() => void) | undefined;
  /** Live share — renders the avatar pin + "Live location until …" footer. */
  live?: boolean | undefined;
  /** Epoch ms the live share expires; drives the "until h:mm AM/PM" label. */
  liveUntilTs?: number | undefined;
  /** Sharer avatar URL used as the live map pin; falls back to a marker. */
  sharerAvatarUrl?: string | undefined;
  /** Message timestamp (epoch ms) shown bottom-right of the footer. */
  timestamp?: number | undefined;
  testID?: string | undefined;
  /** Group chats only — resolved reader list for the "Read by" popover. */
  readers?: ReadByReader[] | undefined;
}

const MAP_WIDTH = 372; // inner card width (376) minus 2px inset each side
const MAP_HEIGHT = 150;
const MAP_ZOOM = 14;

// `readMapsEnv` lives in a platform-split module: the `.web.ts` variant
// reads `import.meta.env` (valid under Vite), while the native base `.ts`
// returns null — keeping the `import.meta` token out of the Hermes bundle
// (it's a hard parse error on react-native-macos).

export interface StaticMapOptions {
  /** Rendered pixel width of the static image. Defaults to the card width. */
  readonly width?: number | undefined;
  /** Rendered pixel height of the static image. Defaults to the card height. */
  readonly height?: number | undefined;
  /** Map zoom level (1–20). Defaults to the card zoom. */
  readonly zoom?: number | undefined;
}

/**
 * Build a static-map image URL centered on the pin. Coordinates are clamped to
 * valid ranges so a bad payload can't produce a malformed request. Returns
 * `null` when no maps key is configured (callers then render a placeholder).
 *
 * Shared by {@link LocationAttachmentCard} (the sent bubble) and
 * `ShareLocationModal` (the in-modal preview) so both honour the same env
 * contract. The key is read from the host env at runtime and is never logged.
 */
export function buildStaticMapUrl(
  lat: number,
  lng: number,
  options: StaticMapOptions = {},
): string | null {
  const cfg = readMapsEnv();
  if (!cfg) {
    return null;
  }
  const safeLat = Math.max(-90, Math.min(90, lat));
  const safeLng = Math.max(-180, Math.min(180, lng));
  const w = Math.round(options.width ?? MAP_WIDTH);
  const h = Math.round(options.height ?? MAP_HEIGHT);
  const zoom = Math.max(1, Math.min(20, Math.round(options.zoom ?? MAP_ZOOM)));
  if (cfg.kind === 'template') {
    // Substitute {lon} {lat} {zoom} {w} {h} {key} placeholders. The key is
    // URL-encoded; coordinates are numeric so they need no escaping.
    return cfg.template
      .replace(/\{lon\}/g, String(safeLng))
      .replace(/\{lat\}/g, String(safeLat))
      .replace(/\{zoom\}/g, String(zoom))
      .replace(/\{w\}/g, String(w))
      .replace(/\{h\}/g, String(h))
      .replace(/\{key\}/g, encodeURIComponent(cfg.key));
  }
  switch (cfg.kind) {
    case 'mapbox':
      return (
        `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/` +
        `pin-s+5b5bd6(${safeLng},${safeLat})/` +
        `${safeLng},${safeLat},${zoom}/${w}x${h}@2x` +
        `?access_token=${encodeURIComponent(cfg.token)}`
      );
    case 'maptiler':
      return (
        `https://api.maptiler.com/maps/streets-v2/static/` +
        `${safeLng},${safeLat},${zoom}/${w}x${h}@2x.png` +
        `?marker=${safeLng},${safeLat}&key=${encodeURIComponent(cfg.token)}`
      );
    case 'google':
    default:
      return (
        `https://maps.googleapis.com/maps/api/staticmap` +
        `?center=${safeLat},${safeLng}&zoom=${zoom}&size=${w}x${h}&scale=2` +
        `&markers=color:0x5b5bd6%7C${safeLat},${safeLng}` +
        `&key=${encodeURIComponent(cfg.token)}`
      );
  }
}

function formatCoord(lat: number, lng: number): string {
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

// "1:40 PM" — the live-share expiry clock shown in the footer label.
function formatClock(ts: number): string {
  const d = new Date(ts);
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m.toString().padStart(2, '0')} ${ampm}`;
}

// "12:33" — the bare message-time shown bottom-right of the self footer
// (no AM/PM; Figma 3629:6788).
function formatShortTime(ts: number): string {
  const d = new Date(ts);
  let h = d.getHours();
  const m = d.getMinutes();
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m.toString().padStart(2, '0')}`;
}

// Initials for the avatar-pin fallback when no avatar URL is available.
function initialsFrom(label: string | undefined): string {
  if (!label) return '·';
  const parts = label.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('') || '·';
}

/**
 * Sent location tile — Figma 3629:6788 (self/live), 3629:7559 (other/live),
 * 3629:7135 (static). A 380-wide, rounded-[16px] outer wrap holds a
 * rounded-[14px] inner card (map + footer) and, below it, a full-width
 * rounded-[8px] action button.
 *
 *   • SELF/outgoing:  outer bg #537EFF, inner card bg #3E6AE6.
 *   • OTHER/incoming: outer bg #15171C, inner card bg #222429.
 *
 * Live shares render the sharer's avatar (rounded-[10px]) as the map pin and
 * a "Live location until {h:mm AM/PM}" footer; static points use a plain pin
 * marker and the place label. Action button label varies by self/live.
 *
 * Tailwind/NativeWind classes lead; inline `style` is used only for the
 * runtime-computed map image URI.
 */
export const LocationAttachmentCard = React.memo<LocationAttachmentCardProps>(
  ({
    lat,
    lng,
    label,
    isSelf,
    onOpen,
    live = false,
    liveUntilTs,
    sharerAvatarUrl,
    timestamp,
    delivered,
    read,
    testID,
    readers,
  }) => {
    // Thread side panel: cap the tile to the provided width.
    const tileMax = useTileMaxWidth();
    const mapUrl = useMemo(
      () => buildStaticMapUrl(lat, lng, { width: MAP_WIDTH, height: MAP_HEIGHT }),
      [lat, lng],
    );
    const coord = useMemo(() => formatCoord(lat, lng), [lat, lng]);

    const title =
      label && label.length > 0 ? label : 'Shared location';

    // Footer label: live → "Live location until 1:40 PM"; static → place label.
    const footerLabel = live
      ? `Live location until ${liveUntilTs !== undefined ? formatClock(liveUntilTs) : '—'}`
      : title;

    // Action-button label by variant. Static points (no live share) match
    // Figma 3629:7135 — no action button below the card.
    const actionLabel = isSelf ? 'Stop sharing' : 'View current location';
    const showAction = live;

    return (
      <View
        // Alignment is owned by the row context — bubble mode wraps tiles in
        // a `justify-end/justify-start` row (self vs other); compact mode
        // renders left in the stacked content column. So the card sets NO
        // self-alignment (no flex-start/flex-end) — it stays left in compact
        // and is pushed right for self only by the bubble row.
        className={`w-[380px] max-w-full rounded-[16px] p-0.5 ${
          isSelf
            ? 'bg-light-accent-brand dark:bg-dark-accent-brand'
            : 'bg-white dark:bg-dark-surface-panel'
        }`}
        style={tileMax !== null ? { maxWidth: tileMax } : undefined}
        testID={testID}
      >
        {/* Inner card — map + footer, clipped to radius 14. */}
        <View
          className={`overflow-hidden rounded-[14px] ${
            isSelf
              ? 'bg-[#3E6AE6]'
              : 'bg-light-surface-secondary dark:bg-dark-surface-elevated'
          }`}
        >
          {/* Map area (372×150) — tapping it opens the location in maps (for
              BOTH static + live shares, self + other, bubble + compact, since
              this card is the single shared renderer). The live-share action
              button below is a separate affordance; the map preview itself was
              previously inert on static shares. */}
          <Pressable
            onPress={onOpen}
            disabled={onOpen === undefined}
            accessibilityRole="button"
            accessibilityLabel="Open location in maps"
            accessibilityHint="Opens this location in a new tab"
            className="h-[150px] w-full overflow-hidden bg-white dark:bg-dark-surface-panel"
          >
            {mapUrl ? (
              <Image
                source={{ uri: mapUrl }}
                className="absolute inset-0 h-full w-full"
                accessibilityIgnoresInvertColors
              />
            ) : (
              <View
                className="flex-1 items-center justify-center gap-1"
                pointerEvents="none"
              >
                <MapPin size={28} color="#B5BAC3" />
                <Text className="text-[11px] leading-[15px] text-[#B5BAC3]">
                  {coord}
                </Text>
              </View>
            )}

            {/* Center pin — avatar for live, marker for static. */}
            <View
              className="absolute inset-0 items-center justify-center"
              pointerEvents="none"
            >
              {live ? (
                <View className="h-8 w-8 items-center justify-center overflow-hidden rounded-[10px] border-2 border-light-accent-brand dark:border-dark-accent-brand bg-white dark:bg-dark-surface-panel">
                  {sharerAvatarUrl ? (
                    <Image
                      source={{ uri: sharerAvatarUrl }}
                      className="h-full w-full"
                      accessibilityIgnoresInvertColors
                    />
                  ) : (
                    <Text className="text-[12px] font-bold text-white">
                      {initialsFrom(label)}
                    </Text>
                  )}
                </View>
              ) : (
                <MapPin size={32} color="#537EFF" fill="#537EFF" />
              )}
            </View>
          </Pressable>

          {/* Footer row — pin + label, timestamp + ticks (self) on the right. */}
          <View className="flex-row items-center gap-1.5 px-2.5 py-2">
            <MapPin size={12} color="#F5F6F8" />
            <Text
              className="flex-1 text-[12px] font-bold text-[#F5F6F8]"
              numberOfLines={1}
            >
              {footerLabel}
            </Text>
            {/* Self footer shows the bare send time + read ticks (Figma
                3629:6788); incoming tiles (3629:7559) show neither. */}
            {isSelf ? (
              <ReadReceiptOverlay
                {...(timestamp !== undefined ? { timeLabel: formatShortTime(timestamp) } : {})}
                textStyle={{ color: 'rgba(255,255,255,0.4)' }}
                {...(delivered !== undefined ? { delivered } : {})}
                {...(read !== undefined ? { read } : {})}
                isSelf={true}
                {...(readers !== undefined ? { readers } : {})}
                showPill={false}
                tickColor={read === true ? '#3CCB4F' : 'rgba(255,255,255,0.4)'}
                popoverPlacement="top"
                popoverAlign="end"
              />
            ) : null}
          </View>
        </View>

        {/* Action button (376×34) below the card. Live shares only — static
            points (3629:7135) end at the card with no button. */}
        {showAction ? (
          <Pressable
            onPress={onOpen}
            accessibilityRole="button"
            accessibilityLabel={actionLabel}
            accessibilityHint="Open in maps"
            className="h-[34px] items-center justify-center rounded-[8px]"
          >
            <Text className="text-[16px] text-[#F5F6F8]">{actionLabel}</Text>
          </Pressable>
        ) : null}
      </View>
    );
  },
);

LocationAttachmentCard.displayName = 'LocationAttachmentCard';
